import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Download, AlertTriangle, FileText } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg, downloadText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { gbp, fmtDate, fmtDateTime } from "@/lib/format";
import { StatusBadge, SourceTag, Sap } from "@/components/StatusBadge";

const Field = ({ label, children }) => (
  <div><div className="label-xs">{label}</div><div className="mt-0.5 text-sm font-medium">{children}</div></div>
);

const Decision = ({ order, onDone }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (kind) => {
    if (kind === "reject" && !note.trim()) return toast.error("Please add a reason for rejection");
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/${kind}`, { note });
      toast.success(`Order ${order.order_number} ${kind === "approve" ? "approved" : "rejected"}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-lg border-2 border-amber-200 bg-amber-50/60 p-4" data-testid="approval-panel">
      <div className="mb-2 text-sm font-semibold">Approval decision</div>
      <Textarea placeholder="Note (required when rejecting)" value={note} onChange={(e) => setNote(e.target.value)} className="bg-white" data-testid="approval-note-input" />
      <div className="mt-3 flex gap-2">
        <Button disabled={busy} onClick={() => act("approve")} className="bg-[#00573F] hover:bg-[#003D2C]" data-testid="order-approve-btn"><Check className="mr-1 h-4 w-4" />Approve order</Button>
        <Button disabled={busy} variant="outline" onClick={() => act("reject")} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="order-reject-btn"><X className="mr-1 h-4 w-4" />Reject</Button>
      </div>
    </div>
  );
};

const Lines = ({ lines }) => (
  <div className="overflow-x-auto rounded-lg border">
    <table className="w-full text-sm" data-testid="order-lines-table">
      <thead className="bg-slate-50 text-left"><tr className="label-xs">
        <th className="px-3 py-2">SAP</th><th className="px-3 py-2">Product</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Price</th><th className="px-3 py-2 text-right">Total</th>
      </tr></thead>
      <tbody>{lines.map((l, i) => (
        <tr key={i} className={`border-t ${l.valid ? "" : "bg-red-50"}`}>
          <td className="px-3 py-2"><Sap code={l.sap_code} /></td>
          <td className="px-3 py-2"><div className="font-medium">{l.title}</div><div className="text-xs text-slate-500">{l.category}</div></td>
          <td className="px-3 py-2 text-right tabular-nums">{l.qty}</td>
          <td className="px-3 py-2 text-right tabular-nums">{gbp(l.price)}</td>
          <td className="px-3 py-2 text-right font-semibold tabular-nums">{gbp(l.line_total)}</td>
        </tr>))}
      </tbody>
    </table>
  </div>
);

const Timeline = ({ history }) => (
  <ol className="relative ml-2 border-l border-slate-200" data-testid="order-history-timeline">
    {history.map((h, i) => (
      <li key={i} className="mb-4 ml-4">
        <span className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full border-2 border-white bg-[#FF0000]" />
        <div className="text-sm font-semibold">{h.action}</div>
        <div className="text-xs text-slate-500">{fmtDateTime(h.at)} · {h.by}</div>
        {h.note && <div className="mt-1 text-xs italic text-slate-600">“{h.note}”</div>}
      </li>))}
  </ol>
);

export const OrderDetailSheet = ({ orderId, onClose, onChanged }) => {
  const { refreshMeta } = useAuth();
  const [order, setOrder] = useState(null);
  const load = () => api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { setOrder(null); if (orderId) load(); }, [orderId]); // eslint-disable-line
  const done = () => { load(); refreshMeta(); onChanged?.(); };
  const dl = async () => {
    const { data } = await api.get(`/orders/${order.id}/txt`);
    downloadText(data, order.file_name || `S${order.store_code}_${order.po_ref}.txt`);
  };
  return (
    <Sheet open={!!orderId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="order-detail-sheet">
        {!order ? <div className="p-6 label-xs"><SheetTitle className="sr-only">Loading order</SheetTitle>Loading order…</div> : (
          <div className="space-y-6">
            <SheetHeader className="text-left">
              <div className="flex items-center gap-2"><SourceTag source={order.source} /><StatusBadge status={order.status} testId="order-detail-status" /></div>
              <SheetTitle className="font-display text-2xl" data-testid="order-detail-number">{order.order_number}</SheetTitle>
              <div className="text-sm text-slate-500">{order.concession_name ? `${order.concession_name} @ ` : ""}{order.store_name} · {order.region}</div>
            </SheetHeader>
            <div className="grid grid-cols-2 gap-4 rounded-lg bg-slate-50 p-4 sm:grid-cols-3">
              <Field label="Ordered">{fmtDate(order.date_ordered)}</Field>
              <Field label="Delivery">{fmtDate(order.delivery_date)}</Field>
              <Field label="PO ref"><span className="font-mono text-xs">{order.po_ref}</span></Field>
              <Field label="Cost centre">{order.cost_centre}</Field>
              <Field label="Account ref">{order.account_ref}</Field>
              <Field label="Created by">{order.created_by_name}</Field>
            </div>
            {order.file_name && <div className="flex items-center gap-2 text-xs text-slate-500"><FileText className="h-4 w-4" />{order.file_name}</div>}
            {order.warnings?.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="order-warnings">
                <div className="mb-1 flex items-center gap-2 font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" />Validation warnings</div>
                <ul className="list-disc pl-5 text-amber-900">{order.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
              </div>
            )}
            {order.can_approve && <Decision order={order} onDone={done} />}
            {!order.can_approve && order.status === "pending_approval" && (
              <div className="rounded-lg border border-[#C5A059]/40 bg-[#C5A059]/10 p-3 text-sm" data-testid="order-readonly-note">
                Awaiting approval by your Store Manager, Regional Manager or Head Office. You can track progress here.
              </div>)}
            <Lines lines={order.lines} />
            <div className="flex items-center justify-between border-t pt-4">
              <Button variant="outline" size="sm" onClick={dl} data-testid="order-download-txt-btn"><Download className="mr-1 h-4 w-4" />Download .txt</Button>
              <div className="text-right"><div className="label-xs">Order total · {order.units} units</div><div className="font-display text-2xl font-bold" data-testid="order-detail-total">{gbp(order.total)}</div></div>
            </div>
            <div><div className="label-xs mb-3">Audit trail</div><Timeline history={order.history} /></div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
